import { computed, inject, Injectable } from '@angular/core';
import { BookmarkStore } from '../../stores/bookmark/bookmark.state';
import { BookmarkCategoryStore } from '../../stores/bookmark-category/bookmark-category.state';
import { Bookmark } from '../../models/bookmark';

/**
 * Bridges Bookmark <-> BookmarkCategory now that the relationship lives only
 * on `BookmarkCategory.bookmarks` (an ordered array of bookmark ids). Neither
 * store can answer "which categories does this bookmark belong to" or
 * "which bookmarks does this category contain" on its own - this service
 * combines both, the same way `TopicHelperService` already combines
 * `BookmarkStore`/`BookmarkCategoryStore`/`StoryStore` for Topic-side questions.
 */
@Injectable({
  providedIn: 'root',
})
export class BookmarkCategoryHelperService {
  readonly bookmarkStore = inject(BookmarkStore);
  readonly bookmarkCategoryStore = inject(BookmarkCategoryStore);

  /**
   * Reverse index built purely from `BookmarkCategoryStore`'s own state:
   * bookmark id -> the ids of every category that lists it.
   */
  private readonly bookmarksIndex = computed(() => {
    const index: Record<string, string[]> = {};
    this.bookmarkCategoryStore.asArray().forEach(category => {
      (category.bookmarks || []).forEach(bookmarkId => {
        (index[bookmarkId] ??= []).push(category._id);
      });
    });
    return index;
  });

  /**
   * The ids of every category the given bookmark belongs to.
   *
   * @param {string} bookmarkId - The bookmark's id.
   * @return {string[]} The category ids referencing it, or an empty array if none do.
   */
  getCategoriesForBookmark(bookmarkId: string): string[] {
    return this.bookmarksIndex()[bookmarkId] || [];
  }

  /**
   * The bookmarks belonging to the given category, hydrated to full `Bookmark`
   * objects and kept in the category's own `bookmarks` array order - that
   * order IS the display order within this category.
   *
   * @param {string} categoryId - The category's id.
   * @return {Bookmark[]} The category's bookmarks in their stored order.
   */
  getBookmarksForCategory(categoryId: string): Bookmark[] {
    const category = this.bookmarkCategoryStore.getById(categoryId);
    const items = this.bookmarkStore.items();
    return (category?.bookmarks || [])
      .map(id => items[id])
      .filter((bookmark): bookmark is Bookmark => !!bookmark);
  }

  /**
   * All bookmarks, grouped by every category id that references them - a
   * bookmark belonging to several categories appears in each of their groups.
   *
   * @return {Record<string, Bookmark[]>} Category id -> its bookmarks, in stored order.
   */
  getGroupedByCategory(): Record<string, Bookmark[]> {
    const grouped: Record<string, Bookmark[]> = {};
    this.bookmarkCategoryStore.asArray().forEach(category => {
      grouped[category._id] = this.getBookmarksForCategory(category._id);
    });
    return grouped;
  }

  /**
   * Private bookmarks that belong to no category, sorted by their own
   * `sorting` field (the only field that still governs order here, since
   * these bookmarks have no category array to encode a position in).
   *
   * @return {Bookmark[]} The private, uncategorized bookmarks.
   */
  getPrivateBookmarks(): Bookmark[] {
    return Object.values(this.bookmarkStore.items())
      .filter(
        bookmark =>
          bookmark.private && !this.getCategoriesForBookmark(bookmark._id).length
      )
      .sort((a, b) => a.sorting - b.sorting);
  }

  /**
   * Non-private bookmarks that belong to no category, sorted by `sorting`.
   *
   * @return {Bookmark[]} The unsorted, uncategorized bookmarks.
   */
  getUnsortedBookmarks(): Bookmark[] {
    return Object.values(this.bookmarkStore.items())
      .filter(
        bookmark =>
          !bookmark.private &&
          !this.getCategoriesForBookmark(bookmark._id).length
      )
      .sort((a, b) => a.sorting - b.sorting);
  }
}
